import React, { useEffect, useState, createContext, useContext } from 'react';
import { KPIBucket } from '../data/types';
import { KPI_BUCKETS } from '../data/mockData';
interface KPIContextType {
  kpis: KPIBucket[];
  addKPI: (kpi: Omit<KPIBucket, 'id' | 'score' | 'trend' | 'previousScore'>) => void;
  updateKPI: (id: string, updates: Partial<KPIBucket>) => void;
  deleteKPI: (id: string) => void;
  generateSignals: (kpiName: string) => Promise<string[]>;
}
const KPIContext = createContext<KPIContextType | undefined>(undefined);
export function KPIProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [kpis, setKpis] = useState<KPIBucket[]>(KPI_BUCKETS);
  const addKPI = (newKPI: Omit<KPIBucket, 'id' | 'score' | 'trend' | 'previousScore'>) => {
    const kpi: KPIBucket = {
      ...newKPI,
      id: `k${Date.now()}`,
      score: Math.floor(Math.random() * (90 - 60) + 60),
      trend: 0,
      previousScore: 0
    };
    setKpis(prev => [...prev, kpi]);
  };
  const updateKPI = (id: string, updates: Partial<KPIBucket>) => {
    setKpis(prev => prev.map(kpi => kpi.id === id ? {
      ...kpi,
      ...updates
    } : kpi));
  };
  const deleteKPI = (id: string) => {
    setKpis(prev => prev.filter(kpi => kpi.id !== id));
  };
  const generateSignals = async (kpiName: string): Promise<string[]> => {
    // Mock AI generation based on keywords
    await new Promise(resolve => setTimeout(resolve, 1500)); // Simulate delay
    const lowerName = kpiName.toLowerCase();
    if (lowerName.includes('work') || lowerName.includes('burnout')) {
      return ['Frequent overtime mentions', 'Deadline pressure complaints', 'Work-life balance concerns', 'Resource allocation requests'];
    }
    if (lowerName.includes('lead') || lowerName.includes('manage')) {
      return ['Manager support feedback', 'Leadership transparency mentions', 'Decision-making clarity', 'Guidance requests'];
    }
    if (lowerName.includes('comm')) {
      return ['Information silo complaints', 'Meeting effectiveness feedback', 'Cross-team alignment issues', 'Update frequency preferences'];
    }
    if (lowerName.includes('cult')) {
      return ['Team bonding mentions', 'Inclusion and diversity feedback', 'Values alignment comments', 'Social event suggestions'];
    }
    // Default generic signals if no keyword match
    return [`${kpiName} satisfaction levels`, `${kpiName} improvement suggestions`, `Positive ${kpiName} feedback`, `Negative ${kpiName} indicators`];
  };
  return <KPIContext.Provider value={{
    kpis,
    addKPI,
    updateKPI,
    deleteKPI,
    generateSignals
  }} data-id="element-151">
      {children}
    </KPIContext.Provider>;
}
export function useKPIs() {
  const context = useContext(KPIContext);
  if (context === undefined) {
    throw new Error('useKPIs must be used within a KPIProvider');
  }
  return context;
}